import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { useEffect } from 'react';
import { ANNOTATION_TYPE } from '@/annotation/annotation.constants';
import { SEEQ_DATASOURCE } from '@/main/app.constants';
import {
  DATASOURCES_TO_EXCLUDE,
  HOME_BREADCRUMB,
  SEARCH_BREADCRUMB,
  SEARCH_RESULT_TYPES,
  type SearchModes,
  TABLE_DEFINITION_SUBTYPES,
} from '@/search/search.constants';
import {
  filterOutInvalidSearchTypes,
  hasInvalidSearchTypes,
  showOnlyTheseDatasources,
  updateTypesAndFiltersForTableDefinitions,
} from '@/search/search.utilities';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@/stores/createStore';
import type { Datasource, SearchBreadcrumb, SelectedSearchItem } from '@/search/search.types';
import type { HierarchyNodeOutputV1 } from '@seeqdev/api/model/HierarchyNodeOutputV1';

export type SEARCH_RESULTS = (typeof SEARCH_RESULT_TYPES)[keyof typeof SEARCH_RESULT_TYPES];

export interface SearchFilters {
  filters: string | string[];
  types: string | string[];
}

export type SearchFilterOptions = {
  workbookId?: string;
  userDatasources?: string[];
  includeNewDatasources?: boolean;
};

export interface SearchPersistedBreadcrumb {
  id: string;
  name?: string;
  type?: string;
  isScalingTableCSVFlow?: boolean;
}

export interface SearchPersistedItem {
  id: string;
}

export type SearchBreadcrumbInput = {
  id: string;
  name?: string;
  type?: string;
  isScalingTableCSVFlow?: boolean;
} & Partial<SearchBreadcrumb>;

export type SearchTypeOption = {
  value: string;
};

export interface SearchStoreState {
  /** Current free-text name filter. */
  nameFilter: string;
  /** Workbook selections used when filtering search results by workbook scope. */
  workbooksFilter: SelectedSearchItem[];
  /** Current free-text description filter. */
  descriptionFilter: string;
  /** Current journal/document text filter. */
  documentFilter: string;
  /** Explicitly selected item types. */
  typeFilter: string[];
  /** Explicitly selected datasource filters. */
  datasourceFilter: Datasource[];
  /** Recently used datasource filters, capped to the latest five. */
  recentDatasourceFilters: Datasource[];
  /** Active search sort choice. */
  sortBy: string;
  /** Asset currently being explored in tree mode. */
  currentAsset: string;
  /** Breadcrumbs shown for the current search mode. */
  breadcrumbs: SearchBreadcrumb[];
  /** Current result items shown in the widget. */
  items: SelectedSearchItem[];
  /** Current high-level search mode. */
  mode: SearchModes;
  /** Whether the name filter should use exact-match regex semantics. */
  isExactName: boolean;
  /** Whether search should only consider locally scoped items. */
  searchLocallyScoped: boolean;
  /** Whether more paginated results are available. */
  hasNextPage: boolean;
  /** Active loading state for search results, if any. */
  searching: SEARCH_RESULTS | undefined;
  /** Whether the next page of results is loading. */
  isPaginating: boolean;
  /** Whether advanced search controls are visible. */
  isAdvancedMode: boolean;
  /** Whether async bootstrapping data has been loaded. */
  isAsyncInitialized: boolean;
  /** Datasources that map to the generic Seeq local datasource. */
  localDatasources: Datasource[];
  /** Datasources available for filtering in the current context. */
  datasources: Datasource[];
  /** Whether select-all is currently active for the visible result set. */
  selectAllChecked: boolean | undefined;
  /** IDs of currently selected result items. */
  selectedItemIds: string[];
  /** ID of the item that should be highlighted in the UI. */
  highlightItemId: string;
  /** ID of the display currently showing a loading spinner. */
  displayLoadingId: string;
  /** Whether datasource preferences should constrain this search. */
  isUsingDatasourcePrefsSearchFilters: boolean;
  /** Selected item table when drilling into hierarchies. */
  selectedItemTable: SelectedSearchItem | undefined;
  /** Selected hierarchy when drilling into hierarchy nodes. */
  selectedHierarchy: SelectedSearchItem | undefined;
  /** Current path inside the selected hierarchy. */
  currentHierarchyPath: HierarchyNodeOutputV1[];
  /** Selected scaling table for scaling-table drill-in flows. */
  selectedScalingTable: SelectedSearchItem | undefined;
  /** Selected row within the current scaling table drill-in flow. */
  selectedScalingTableRow: SelectedSearchItem | undefined;
  /** View mode when navigating inside a scaling table: flat list or hierarchical tree. */
  scalingTableViewMode: 'flat' | 'tree';
}

export type SearchDehydratedState = Pick<
  SearchStoreState,
  | 'nameFilter'
  | 'isExactName'
  | 'searchLocallyScoped'
  | 'descriptionFilter'
  | 'typeFilter'
  | 'datasourceFilter'
  | 'sortBy'
  | 'isAdvancedMode'
  | 'mode'
  | 'currentAsset'
  | 'selectAllChecked'
  | 'selectedItemIds'
  | 'selectedItemTable'
  | 'selectedHierarchy'
  | 'selectedScalingTable'
  | 'selectedScalingTableRow'
  | 'currentHierarchyPath'
  | 'recentDatasourceFilters'
  | 'isUsingDatasourcePrefsSearchFilters'
  | 'scalingTableViewMode'
> & {
  breadcrumbs?: SearchPersistedBreadcrumb[];
  items?: SearchPersistedItem[];
};

export interface SearchStoreActions {
  setIsAdvancedMode: (isAdvancedMode: boolean) => void;
  clearFilters: () => void;
  clearHighlightItem: () => void;
  setMode: (mode: SearchModes) => void;
  getSearchFilters: (searchTypes?: string[], options?: SearchFilterOptions) => SearchFilters;
  setAsyncInitialized: (isAsyncInitialized: boolean) => void;
  initiateSearch: () => void;
  initiateUsagesSearch: () => void;
  finishSearch: () => void;
  setNameFilter: (nameFilter: string) => void;
  setWorkbooksFilter: (workbooksFilter: SelectedSearchItem[]) => void;
  setSearchLocallyScoped: (searchLocallyScoped: boolean) => void;
  setIsExactName: (isExactName: boolean) => void;
  setDescriptionFilter: (descriptionFilter: string) => void;
  setDocumentFilter: (documentFilter: string) => void;
  setBreadcrumbs: (breadcrumbs: SearchBreadcrumbInput[]) => void;
  appendOrReplaceBreadcrumb: (breadcrumb: SearchBreadcrumbInput) => void;
  setSelectedTypes: (types: SearchTypeOption[]) => void;
  exploreAsset: (asset: string) => void;
  exploreScalingTable: () => void;
  exploreItemHierarchy: () => void;
  setSelectedDatasources: (datasources: Array<{ label: string; value: Datasource }>) => void;
  setResultsSuccess: (items: SelectedSearchItem[], hasNextPage: boolean) => void;
  setIsPaginating: () => void;
  resetSoft: () => void;
  setDatasources: (datasources: Datasource[]) => void;
  setSortBy: (sortBy: string) => void;
  setHighlightItem: (id: string) => void;
  setDisplayLoading: (id: string) => void;
  setIsUsingDatasourcePrefsSearchFilters: (value: boolean) => void;
  setSelectedItemTable: (itemTable: SelectedSearchItem | undefined) => void;
  setSelectedHierarchy: (hierarchy: SelectedSearchItem | undefined) => void;
  setCurrentHierarchyPath: (path: HierarchyNodeOutputV1[]) => void;
  setSelectedScalingTable: (scalingTable: SelectedSearchItem | undefined) => void;
  setSelectedScalingTableRow: (scalingTableRow: SelectedSearchItem | undefined) => void;
  setScalingTableViewMode: (mode: 'flat' | 'tree') => void;
  toggleSelectAll: (items: Array<{ id: string }>) => void;
  toggleSelectedItem: (itemId: string, items?: Array<{ id: string }>) => void;
  clearSelectedItems: () => void;
  setSelectAll: (isAllSelected: boolean | undefined) => void;
  rehydrate: (dehydratedState: Partial<SearchDehydratedState>) => void;
  reset: () => void;
}

const LOCAL_DATASOURCE_CLASSES = [
  SeeqNames.LocalDatasources.FileSignalStorage.DatasourceClass,
  SeeqNames.LocalDatasources.PostgresDatums.DatasourceClass,
  SeeqNames.LocalDatasources.SeeqCalculationItemStorage.DatasourceClass,
  SeeqNames.LocalDatasources.SeeqMetadataItemStorage.DatasourceClass,
];

export const buildInitialSearchState = (
  preservedState?: Partial<
    Pick<SearchStoreState, 'recentDatasourceFilters' | 'localDatasources' | 'datasources' | 'scalingTableViewMode'>
  >,
): SearchStoreState => ({
  nameFilter: '',
  workbooksFilter: [],
  descriptionFilter: '',
  documentFilter: '',
  typeFilter: [],
  datasourceFilter: [],
  recentDatasourceFilters: preservedState?.recentDatasourceFilters ?? [],
  sortBy: '',
  currentAsset: '',
  breadcrumbs: [],
  items: [],
  mode: 'overview',
  isExactName: false,
  searchLocallyScoped: false,
  hasNextPage: false,
  searching: undefined,
  isPaginating: false,
  isAdvancedMode: false,
  isAsyncInitialized: false,
  localDatasources: preservedState?.localDatasources ?? [],
  datasources: preservedState?.datasources ?? [],
  selectAllChecked: false,
  selectedItemIds: [],
  highlightItemId: '',
  displayLoadingId: '',
  isUsingDatasourcePrefsSearchFilters: true,
  selectedItemTable: undefined,
  selectedHierarchy: undefined,
  currentHierarchyPath: [],
  selectedScalingTable: undefined,
  selectedScalingTableRow: undefined,
  scalingTableViewMode: 'flat',
});

const initialState = buildInitialSearchState();

const toSearchBreadcrumb = (breadcrumb: SearchBreadcrumbInput): SearchBreadcrumb => ({
  ...breadcrumb,
  name: breadcrumb.name!,
  type: breadcrumb.type!,
});

export const clearSearchFiltersInDraft = (draft: SearchStoreState) => {
  draft.nameFilter = '';
  draft.descriptionFilter = '';
  draft.documentFilter = '';
  draft.typeFilter = [];
  draft.datasourceFilter = [];
  draft.sortBy = '';
};

export const clearSearchHighlightInDraft = (draft: SearchStoreState) => {
  draft.highlightItemId = '';
};

export const setModeInDraft = (draft: SearchStoreState, mode: SearchModes) => {
  if (mode === draft.mode) {
    return;
  }

  if (mode === 'overview') {
    draft.searching = undefined;
    draft.isPaginating = false;
    draft.currentAsset = '';
    draft.breadcrumbs = [];
    draft.items = [];
    clearSearchFiltersInDraft(draft);
  } else if (mode === 'pinned') {
    draft.breadcrumbs = [
      HOME_BREADCRUMB,
      {
        name: 'SEARCH_DATA.PINNED',
        id: 'PINNED',
        type: 'VIEW_MODE',
      },
    ];
    clearSearchFiltersInDraft(draft);
    draft.isAdvancedMode = false;
  } else if (mode === 'recent') {
    draft.breadcrumbs = [
      HOME_BREADCRUMB,
      {
        name: 'SEARCH_DATA.RECENTLY_ACCESSED',
        id: 'RECENTLY_ACCESSED',
        type: 'VIEW_MODE',
      },
    ];
    draft.isAdvancedMode = false;
    clearSearchFiltersInDraft(draft);
  } else if (mode === 'search') {
    draft.breadcrumbs =
      draft.mode === 'tree' ? draft.breadcrumbs.concat(SEARCH_BREADCRUMB) : [HOME_BREADCRUMB, SEARCH_BREADCRUMB];
  } else if (mode === 'asset-groups') {
    draft.breadcrumbs = [
      HOME_BREADCRUMB,
      {
        name: 'SEARCH_DATA.ASSET_GROUPS',
        id: 'ASSET_GROUPS',
        type: 'VIEW_MODE',
      },
    ];
    draft.isAdvancedMode = false;
    clearSearchFiltersInDraft(draft);
  } else if (mode === 'scaling-tables') {
    draft.breadcrumbs = [
      HOME_BREADCRUMB,
      {
        name: 'SEARCH_DATA.SCALING',
        id: 'SCALING',
        type: 'VIEW_MODE',
      },
    ];
    draft.isAdvancedMode = false;
    clearSearchFiltersInDraft(draft);
  } else if (mode === 'item-tables') {
    draft.breadcrumbs = [
      HOME_BREADCRUMB,
      {
        name: 'SEARCH_DATA.ITEM_TABLES',
        id: 'ITEM_TABLES',
        type: 'VIEW_MODE',
      },
    ];
    draft.isAdvancedMode = false;
    clearSearchFiltersInDraft(draft);
  } else if (mode === 'usages') {
    clearSearchFiltersInDraft(draft);
  } else if (mode === 'scaling-tables-tree' || mode === 'item-hierarchies') {
    draft.items = [];
  }

  draft.mode = mode;
};

const getFilterTransform = (isExactName: boolean, filterKey: string): ((value: string) => string) => {
  const filterTransforms: Record<string, (value: string) => string> = {
    name: isExactName ? (value: string) => `/^${value}$/i` : _.identity,
  };

  return filterTransforms[filterKey] ?? _.identity;
};

export const buildSearchFilters = (
  state: SearchStoreState,
  searchTypes: string[] = [],
  { workbookId = '', userDatasources = [], includeNewDatasources = false }: SearchFilterOptions = {},
): SearchFilters => {
  let types: string | string[];
  let filters: string | string[];

  const andFilters = _.chain(['name', 'description'])
    .map((filterKey) => [filterKey, state[`${filterKey}Filter` as 'nameFilter' | 'descriptionFilter']])
    .reject(([_filterKey, value]) => _.isEmpty(value))
    .map(([filterKey, value]) => `${filterKey}~=${getFilterTransform(state.isExactName, filterKey)(value as string)}`)
    .join('&&')
    .value();

  let datasourceFilter = state.datasourceFilter;
  if (_.isEmpty(datasourceFilter) && state.isUsingDatasourcePrefsSearchFilters) {
    datasourceFilter = showOnlyTheseDatasources(workbookId, state.datasources, userDatasources, {
      includeNewDatasources,
    });
    if (datasourceFilter.length === state.datasources.length) {
      datasourceFilter = [];
    }
  }
  if (_.some(datasourceFilter, (datasource) => datasource.datasourceClass === SEEQ_DATASOURCE.datasourceClass)) {
    datasourceFilter = datasourceFilter.concat(state.localDatasources);
    datasourceFilter = _.reject(
      datasourceFilter,
      (datasource) => datasource.datasourceClass === SEEQ_DATASOURCE.datasourceClass,
    );
  }

  const orFilters = _.map(
    datasourceFilter,
    (datasource: Datasource) =>
      `datasource class==${datasource.datasourceClass}&&datasource id==${datasource.datasourceId}&&${andFilters}`,
  );

  if (!_.isEmpty(state.documentFilter)) {
    filters = `Plain Text Document~=${state.documentFilter}`;
    types = [ANNOTATION_TYPE.JOURNAL];
  } else {
    filters = orFilters.length ? orFilters : andFilters;
    types = _.isEmpty(state.typeFilter) ? (filterOutInvalidSearchTypes(searchTypes) ?? []) : state.typeFilter;
  }

  const newTypesAndFilters = updateTypesAndFiltersForTableDefinitions(types, filters);
  return {
    filters: newTypesAndFilters.updatedFilters,
    types: newTypesAndFilters.updatedTypes,
  };
};

export const setSearchDatasourcesInDraft = (draft: SearchStoreState, datasources: Datasource[]) => {
  draft.localDatasources = _.filter(datasources, (datasource) =>
    _.includes(LOCAL_DATASOURCE_CLASSES, datasource.datasourceClass),
  );

  draft.datasources = _.chain(datasources)
    .map(({ id, name, type, datasourceId, datasourceClass, scopedTo }) => ({
      id,
      name,
      type,
      datasourceId,
      datasourceClass,
      scopedTo,
    }))
    .reject((datasource) => _.includes(DATASOURCES_TO_EXCLUDE, datasource.datasourceClass))
    .reject((datasource) => _.includes(LOCAL_DATASOURCE_CLASSES, datasource.datasourceClass))
    .concat(SEEQ_DATASOURCE)
    .value();
};

export const dehydrateSearchState = (state: SearchStoreState): SearchDehydratedState => {
  const dehydratedState: SearchDehydratedState = {
    nameFilter: state.nameFilter,
    isExactName: state.isExactName,
    searchLocallyScoped: state.searchLocallyScoped,
    descriptionFilter: state.descriptionFilter,
    typeFilter: state.typeFilter,
    datasourceFilter: state.datasourceFilter,
    sortBy: state.sortBy,
    isAdvancedMode: state.isAdvancedMode,
    mode: state.mode,
    currentAsset: state.currentAsset,
    selectAllChecked: state.selectAllChecked,
    selectedItemIds: state.selectedItemIds,
    selectedItemTable: state.selectedItemTable,
    selectedHierarchy: state.selectedHierarchy,
    selectedScalingTable: state.selectedScalingTable,
    selectedScalingTableRow: state.selectedScalingTableRow,
    currentHierarchyPath: state.currentHierarchyPath,
    recentDatasourceFilters: state.recentDatasourceFilters,
    isUsingDatasourcePrefsSearchFilters: state.isUsingDatasourcePrefsSearchFilters,
    scalingTableViewMode: state.scalingTableViewMode,
  };

  if (dehydratedState.mode === 'usages') {
    dehydratedState.breadcrumbs = state.breadcrumbs.map(({ id }) => ({ id }));
  }

  if (hasInvalidSearchTypes(dehydratedState.typeFilter)) {
    dehydratedState.typeFilter = dehydratedState.typeFilter.filter(
      (type: string) => !TABLE_DEFINITION_SUBTYPES.includes(type),
    );
  }

  if (
    dehydratedState.mode === 'scaling-tables-tree' ||
    dehydratedState.mode === 'item-hierarchies' ||
    dehydratedState.mode === 'hierarchy-nodes'
  ) {
    dehydratedState.breadcrumbs = state.breadcrumbs.map(({ id, name, type, isScalingTableCSVFlow }) => ({
      id,
      name,
      type,
      isScalingTableCSVFlow,
    }));
    dehydratedState.items = state.items.map(({ id }) => ({ id }));
  }

  return dehydratedState;
};

const searchStoreActionsFactory: ActionsFactory<SearchStoreState, SearchStoreActions> = (set, get, resetState) => ({
  setIsAdvancedMode: (isAdvancedMode) =>
    set((draft) => {
      draft.isAdvancedMode = isAdvancedMode;
    }),

  clearFilters: () =>
    set((draft) => {
      clearSearchFiltersInDraft(draft);
    }),

  clearHighlightItem: () =>
    set((draft) => {
      clearSearchHighlightInDraft(draft);
    }),

  setMode: (mode) =>
    set((draft) => {
      setModeInDraft(draft, mode);
    }),

  getSearchFilters: (searchTypes = [], options = {}) => buildSearchFilters(get(), searchTypes, options),

  setAsyncInitialized: (isAsyncInitialized) =>
    set((draft) => {
      draft.isAsyncInitialized = isAsyncInitialized;
    }),

  initiateSearch: () =>
    set((draft) => {
      draft.searching = SEARCH_RESULT_TYPES.ITEMS;
      draft.isPaginating = false;
      draft.items = [];
      draft.selectAllChecked = false;
      draft.selectedItemIds = [];
      setModeInDraft(draft, 'search');
    }),

  initiateUsagesSearch: () =>
    set((draft) => {
      draft.searching = SEARCH_RESULT_TYPES.ITEMS;
      draft.isPaginating = false;
      draft.items = [];
      setModeInDraft(draft, 'usages');
    }),

  finishSearch: () =>
    set((draft) => {
      draft.searching = undefined;
      draft.isPaginating = false;
    }),

  setNameFilter: (nameFilter) =>
    set((draft) => {
      draft.nameFilter = nameFilter;
    }),

  setWorkbooksFilter: (workbooksFilter) =>
    set((draft) => {
      draft.workbooksFilter = workbooksFilter;
    }),

  setSearchLocallyScoped: (searchLocallyScoped) =>
    set((draft) => {
      draft.searchLocallyScoped = searchLocallyScoped;
    }),

  setIsExactName: (isExactName) =>
    set((draft) => {
      draft.isExactName = isExactName;
    }),

  setDescriptionFilter: (descriptionFilter) =>
    set((draft) => {
      draft.descriptionFilter = descriptionFilter;
    }),

  setDocumentFilter: (documentFilter) =>
    set((draft) => {
      draft.documentFilter = documentFilter;
    }),

  setBreadcrumbs: (breadcrumbs) =>
    set((draft) => {
      draft.breadcrumbs = breadcrumbs.map(toSearchBreadcrumb);
    }),

  appendOrReplaceBreadcrumb: (breadcrumb) =>
    set((draft) => {
      const normalizedBreadcrumb = toSearchBreadcrumb(breadcrumb);
      const index = _.findIndex(draft.breadcrumbs, ['id', normalizedBreadcrumb.id]);
      if (index >= 0) {
        draft.breadcrumbs.splice(index + 1);
      } else {
        draft.breadcrumbs.push(normalizedBreadcrumb);
      }
    }),

  setSelectedTypes: (types) =>
    set((draft) => {
      draft.typeFilter = _.map(types, (type) => type.value);
    }),

  exploreAsset: (asset) =>
    set((draft) => {
      draft.searching = SEARCH_RESULT_TYPES.ASSETS;
      draft.mode = asset === '' ? 'overview' : 'tree';
      draft.currentAsset = asset;
      draft.items = [];
      clearSearchHighlightInDraft(draft);
    }),

  exploreScalingTable: () =>
    set((draft) => {
      draft.searching = SEARCH_RESULT_TYPES.ASSETS;
      draft.items = [];
      clearSearchHighlightInDraft(draft);
    }),

  exploreItemHierarchy: () =>
    set((draft) => {
      draft.searching = SEARCH_RESULT_TYPES.ASSETS;
      draft.items = [];
      clearSearchHighlightInDraft(draft);
    }),

  setSelectedDatasources: (datasources) =>
    set((draft) => {
      draft.datasourceFilter = _.map(datasources, (datasource) => datasource.value);
    }),

  setResultsSuccess: (items, hasNextPage) =>
    set((draft) => {
      draft.items = _.uniqBy([...draft.items, ...items], 'id');
      draft.hasNextPage = hasNextPage;
      draft.recentDatasourceFilters = _.chain(draft.recentDatasourceFilters)
        .concat(draft.datasourceFilter)
        .uniqBy('id')
        .slice(-5)
        .value();
    }),

  setIsPaginating: () =>
    set((draft) => {
      draft.isPaginating = true;
    }),

  resetSoft: () =>
    set((draft) => {
      Object.assign(
        draft,
        buildInitialSearchState({
          recentDatasourceFilters: draft.recentDatasourceFilters,
          localDatasources: draft.localDatasources,
          datasources: draft.datasources,
        }),
      );
    }),

  setDatasources: (datasources) =>
    set((draft) => {
      setSearchDatasourcesInDraft(draft, datasources);
    }),

  setSortBy: (sortBy) =>
    set((draft) => {
      draft.sortBy = sortBy;
    }),

  setHighlightItem: (id) =>
    set((draft) => {
      draft.highlightItemId = id;
    }),

  setDisplayLoading: (id) =>
    set((draft) => {
      draft.displayLoadingId = id;
    }),

  setIsUsingDatasourcePrefsSearchFilters: (value) =>
    set((draft) => {
      draft.isUsingDatasourcePrefsSearchFilters = value;
    }),

  setSelectedItemTable: (itemTable) =>
    set((draft) => {
      draft.selectedItemTable = itemTable;
    }),

  setSelectedHierarchy: (hierarchy) =>
    set((draft) => {
      draft.selectedHierarchy = hierarchy;
    }),

  setCurrentHierarchyPath: (path) =>
    set((draft) => {
      draft.currentHierarchyPath = path;
    }),

  setSelectedScalingTable: (scalingTable) =>
    set((draft) => {
      draft.selectedScalingTable = scalingTable;
    }),

  setSelectedScalingTableRow: (scalingTableRow) =>
    set((draft) => {
      draft.selectedScalingTableRow = scalingTableRow;
    }),

  setScalingTableViewMode: (mode) =>
    set((draft) => {
      draft.scalingTableViewMode = mode;
    }),

  toggleSelectAll: (items) =>
    set((draft) => {
      const selectAll = draft.selectAllChecked;
      draft.selectAllChecked = !selectAll;
      draft.selectedItemIds = selectAll ? [] : _.map(items, 'id');
    }),

  toggleSelectedItem: (itemId, items) =>
    set((draft) => {
      const itemIdx = _.indexOf(draft.selectedItemIds, itemId);

      if (itemIdx > -1) {
        _.pullAt(draft.selectedItemIds, itemIdx);
        if (draft.selectAllChecked) {
          draft.selectAllChecked = undefined;
        }
      } else {
        draft.selectedItemIds.push(itemId);
        if (draft.selectedItemIds.length === (items?.length ?? 0) && !draft.selectAllChecked) {
          draft.selectAllChecked = true;
        }
      }
    }),

  clearSelectedItems: () =>
    set((draft) => {
      draft.selectAllChecked = false;
      draft.selectedItemIds = [];
    }),

  setSelectAll: (isAllSelected) =>
    set((draft) => {
      draft.selectAllChecked = isAllSelected;
    }),

  rehydrate: (dehydratedState) =>
    set((draft) => {
      Object.assign(draft, dehydratedState);
    }),

  reset: () => {
    resetState();
  },
});

const [SearchStoreProvider, useSearchStoreInstance, useSearchStoreSelector] = createStore(
  'Search',
  initialState,
  searchStoreActionsFactory,
);

export { SearchStoreProvider };

export const useSearchNameFilter = () => useSearchStoreSelector((state) => state.nameFilter);
export const useSearchWorkbooksFilter = () => useSearchStoreSelector((state) => state.workbooksFilter);
export const useSearchDescriptionFilter = () => useSearchStoreSelector((state) => state.descriptionFilter);
export const useSearchDocumentFilter = () => useSearchStoreSelector((state) => state.documentFilter);
export const useSearchTypeFilter = () => useSearchStoreSelector((state) => state.typeFilter);
export const useSearchDatasourceFilter = () => useSearchStoreSelector((state) => state.datasourceFilter);
export const useSearchRecentDatasourceFilters = () => useSearchStoreSelector((state) => state.recentDatasourceFilters);
export const useSearchSortBy = () => useSearchStoreSelector((state) => state.sortBy);
export const useSearchCurrentAsset = () => useSearchStoreSelector((state) => state.currentAsset);
export const useSearchBreadcrumbs = () => useSearchStoreSelector((state) => state.breadcrumbs);
export const useSearchItems = () => useSearchStoreSelector((state) => state.items);
export const useSearchMode = () => useSearchStoreSelector((state) => state.mode);
export const useSearchIsExactName = () => useSearchStoreSelector((state) => state.isExactName);
export const useSearchLocallyScoped = () => useSearchStoreSelector((state) => state.searchLocallyScoped);
export const useSearchHasNextPage = () => useSearchStoreSelector((state) => state.hasNextPage);
export const useSearchSearching = () => useSearchStoreSelector((state) => state.searching);
export const useSearchIsPaginating = () => useSearchStoreSelector((state) => state.isPaginating);
export const useSearchIsAdvancedMode = () => useSearchStoreSelector((state) => state.isAdvancedMode);
export const useSearchIsAsyncInitialized = () => useSearchStoreSelector((state) => state.isAsyncInitialized);
export const useSearchLocalDatasources = () => useSearchStoreSelector((state) => state.localDatasources);
export const useSearchDatasources = () => useSearchStoreSelector((state) => state.datasources);
export const useSearchSelectAllChecked = () => useSearchStoreSelector((state) => state.selectAllChecked);
export const useSearchSelectedItemIds = () => useSearchStoreSelector((state) => state.selectedItemIds);
export const useSearchHighlightItemId = () => useSearchStoreSelector((state) => state.highlightItemId);
export const useSearchDisplayLoadingId = () => useSearchStoreSelector((state) => state.displayLoadingId);
export const useSearchIsUsingDatasourcePrefsSearchFilters = () =>
  useSearchStoreSelector((state) => state.isUsingDatasourcePrefsSearchFilters);
export const useSearchSelectedItemTable = () => useSearchStoreSelector((state) => state.selectedItemTable);
export const useSearchSelectedHierarchy = () => useSearchStoreSelector((state) => state.selectedHierarchy);
export const useSearchCurrentHierarchyPath = () => useSearchStoreSelector((state) => state.currentHierarchyPath);
export const useSearchSelectedScalingTable = () => useSearchStoreSelector((state) => state.selectedScalingTable);
export const useSearchSelectedScalingTableRow = () => useSearchStoreSelector((state) => state.selectedScalingTableRow);
export const useScalingTableViewMode = () => useSearchStoreSelector((state) => state.scalingTableViewMode);

export const useSearchActions = () => useSearchStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateSearchStore() {
  return createStoreInstance<SearchStoreState, SearchStoreActions>(initialState, searchStoreActionsFactory);
}

export const usePersistSearchStore = () => {
  const register = useRegisterPersistedStore();
  const store = useSearchStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqSearchStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateSearchState,
    });
  }, [register, store]);
};
